import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Building2, ArrowLeft, Shield, Box, Database, BarChart3,
  Webhook, Brain, Key, Loader2, Save, RotateCcw, Check, AlertCircle,
  ChevronDown, ChevronRight, Info, Crown, Star, Sparkles, Award
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';

// Module definitions with categories and dependencies
const MODULE_CATEGORIES = {
  core: {
    name: 'Core Modules',
    description: 'Essential platform functionality',
    modules: [
      { 
        key: 'scope_requirements', 
        name: 'Scope Requirements', 
        description: 'Requirements hierarchy management with themes, epics, and features',
        icon: Box,
        minTier: 'starter',
        dependencies: []
      },
      { 
        key: 'baseline_management', 
        name: 'Baseline Management', 
        description: 'WBS baselines, versioning, and comparison tools',
        icon: Database,
        minTier: 'starter',
        dependencies: ['scope_requirements']
      },
    ]
  },
  governance: {
    name: 'Governance',
    description: 'Approval workflows and change control',
    modules: [
      { 
        key: 'governance_flow', 
        name: 'Governance & Approvals', 
        description: 'Change control, approval workflows, and audit trails',
        icon: Shield,
        minTier: 'business',
        dependencies: ['scope_requirements']
      },
    ]
  },
  reporting: {
    name: 'Reporting & Analytics',
    description: 'Dashboards and insights',
    modules: [
      { 
        key: 'reporting', 
        name: 'Reporting Suite', 
        description: 'Executive dashboards, portfolio analytics, and custom reports',
        icon: BarChart3,
        minTier: 'business',
        dependencies: []
      },
    ]
  },
  integrations: {
    name: 'Integrations',
    description: 'External system connectivity',
    modules: [
      { 
        key: 'integrations', 
        name: 'Integrations Hub', 
        description: 'Azure DevOps, Jira, Teams, WhatsApp, and SSO connectivity',
        icon: Webhook,
        minTier: 'enterprise',
        dependencies: []
      },
    ]
  },
  ai: {
    name: 'AI & Automation',
    description: 'AI-powered capabilities',
    modules: [
      { 
        key: 'ai_assistance', 
        name: 'AI Copilot', 
        description: 'AI-powered scoping, document extraction, and suggestions',
        icon: Brain,
        minTier: 'business',
        dependencies: ['scope_requirements']
      },
    ]
  },
  api: {
    name: 'API & Developer',
    description: 'Programmatic access',
    modules: [
      { 
        key: 'api_access', 
        name: 'API Access', 
        description: 'REST API for custom integrations and automation',
        icon: Key,
        minTier: 'enterprise_plus',
        dependencies: []
      },
    ]
  }
};

// Tier configuration for defaults
const TIER_CONFIG = {
  starter: {
    name: 'Starter',
    icon: Star,
    color: 'gray',
    defaultModules: ['scope_requirements']
  },
  business: {
    name: 'Business',
    icon: Crown,
    color: 'blue',
    defaultModules: ['scope_requirements', 'baseline_management', 'governance_flow', 'reporting', 'ai_assistance']
  },
  enterprise: {
    name: 'Enterprise',
    icon: Sparkles,
    color: 'purple',
    defaultModules: ['scope_requirements', 'baseline_management', 'governance_flow', 'reporting', 'integrations', 'ai_assistance']
  },
  enterprise_plus: {
    name: 'Enterprise+',
    icon: Award,
    color: 'amber',
    defaultModules: ['scope_requirements', 'baseline_management', 'governance_flow', 'reporting', 'integrations', 'ai_assistance', 'api_access']
  }
};

// Flatten all modules for easy lookup
const ALL_MODULES = Object.values(MODULE_CATEGORIES).flatMap(cat => cat.modules);

const TenantModulesPage = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [tier, setTier] = useState('starter');
  const [enabledModules, setEnabledModules] = useState({});
  const [originalModules, setOriginalModules] = useState({});
  const [expandedCategories, setExpandedCategories] = useState(['core', 'governance', 'reporting', 'ai']);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadTenantConfig();
  }, [tenantId]);

  useEffect(() => {
    // Check for changes
    const changed = JSON.stringify(enabledModules) !== JSON.stringify(originalModules);
    setHasChanges(changed);
  }, [enabledModules, originalModules]);

  const loadTenantConfig = async () => {
    setLoading(true);
    try {
      // Get tenant details
      const tenantResponse = await api.getTenant(tenantId);
      const tenantData = tenantResponse.data;
      setTenant(tenantData);
      
      // Get tenant configuration
      const configResponse = await api.getTenantConfig(tenantId);
      const config = configResponse.data;
      
      const currentTier = config.tier || tenantData.billing?.tier || 'starter';
      setTier(currentTier);
      
      // Load features - use saved features or tier defaults
      const savedFeatures = config.features || {};
      const tierDefaults = TIER_CONFIG[currentTier]?.defaultModules || [];
      
      // If no features saved, use tier defaults
      const features = Object.keys(savedFeatures).length > 0 
        ? savedFeatures 
        : tierDefaults.reduce((acc, key) => ({ ...acc, [key]: true }), {});
      
      setEnabledModules(features);
      setOriginalModules(features);
    } catch (err) {
      console.error('Failed to load tenant config:', err);
      toast.error('Failed to load tenant configuration');
      navigate('/platform/tenants');
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (moduleKey) => {
    const module = ALL_MODULES.find(m => m.key === moduleKey);
    const newValue = !enabledModules[moduleKey];
    
    const newModules = { ...enabledModules, [moduleKey]: newValue };
    
    // If enabling, also enable dependencies
    if (newValue && module?.dependencies) {
      module.dependencies.forEach(dep => {
        newModules[dep] = true;
      });
      if (module.dependencies.length > 0) {
        const depNames = module.dependencies.map(d => ALL_MODULES.find(m => m.key === d)?.name).join(', ');
        toast.info(`Also enabled: ${depNames}`);
      }
    }
    
    // If disabling, warn about dependents
    if (!newValue) {
      const dependents = ALL_MODULES.filter(m => m.dependencies.includes(moduleKey) && newModules[m.key]);
      if (dependents.length > 0) {
        toast.warning(`"${module.name}" is required by: ${dependents.map(d => d.name).join(', ')}`);
      }
    }
    
    setEnabledModules(newModules);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateTenantConfig(tenantId, {
        features: enabledModules
      });
      
      setOriginalModules(enabledModules);
      toast.success('Module settings saved successfully');
    } catch (err) {
      console.error('Failed to save modules:', err);
      toast.error('Failed to save module settings');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToTierDefaults = () => {
    const tierDefaults = TIER_CONFIG[tier]?.defaultModules || [];
    const defaultFeatures = tierDefaults.reduce((acc, key) => ({ ...acc, [key]: true }), {});
    
    // Reset all modules to false first, then enable tier defaults
    const resetFeatures = {};
    ALL_MODULES.forEach(m => {
      resetFeatures[m.key] = tierDefaults.includes(m.key);
    });
    
    setEnabledModules(resetFeatures);
    toast.info(`Reset to ${TIER_CONFIG[tier]?.name} tier defaults`);
  };

  const toggleCategory = (catKey) => {
    setExpandedCategories(prev => 
      prev.includes(catKey) 
        ? prev.filter(c => c !== catKey)
        : [...prev, catKey]
    );
  };

  const getTierBadgeColor = (minTier) => {
    const colors = {
      starter: 'bg-gray-100 text-gray-600',
      business: 'bg-blue-100 text-blue-600',
      enterprise: 'bg-purple-100 text-purple-600',
      enterprise_plus: 'bg-amber-100 text-amber-600'
    };
    return colors[minTier] || colors.starter;
  };

  const isTierDefault = (moduleKey) => {
    return TIER_CONFIG[tier]?.defaultModules.includes(moduleKey);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  const enabledCount = Object.values(enabledModules).filter(Boolean).length;
  const TierIcon = TIER_CONFIG[tier]?.icon || Star;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Platform Admin Context Banner */}
        <div className="mb-4 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-3">
          <Shield className="w-5 h-5 text-slate-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-slate-700">Platform Admin</p>
            <p className="text-xs text-slate-500">
              You are configuring modules as a <strong>Platform Administrator</strong>. 
              Changes here affect tenant capabilities globally. Tenant admins configure their own preferences separately.
            </p>
          </div>
        </div>
        
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/platform/tenants')}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
            data-testid="back-to-tenants"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Tenant Directory
          </button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Module Licensing</h1>
                <p className="text-sm text-gray-500">{tenant?.tenant?.name || 'Loading...'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={handleResetToTierDefaults}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="reset-tier-defaults-btn"
              >
                <RotateCcw className="w-4 h-4" />
                Reset to Tier Defaults
              </button>
              
              <button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  hasChanges 
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700' 
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
                data-testid="save-modules-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
        
        {/* Tier & Stats Bar */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                tier === 'starter' ? 'bg-gray-100 text-gray-700' :
                tier === 'business' ? 'bg-blue-100 text-blue-700' :
                tier === 'enterprise' ? 'bg-purple-100 text-purple-700' :
                'bg-amber-100 text-amber-700'
              }`}>
                <TierIcon className="w-4 h-4" />
                <span className="font-medium">{TIER_CONFIG[tier]?.name} Tier</span>
              </div>
              <div className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">{enabledCount}</span> of {ALL_MODULES.length} modules enabled
              </div>
            </div>
            {hasChanges && (
              <span className="flex items-center gap-1 text-yellow-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                Unsaved changes
              </span>
            )}
          </div>
        </div>
        
        {/* Module Categories */}
        <div className="space-y-4">
          {Object.entries(MODULE_CATEGORIES).map(([catKey, category]) => {
            const isExpanded = expandedCategories.includes(catKey);
            const enabledInCategory = category.modules.filter(m => enabledModules[m.key]).length;
            
            return (
              <div 
                key={catKey} 
                className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                data-testid={`category-${catKey}`}
              >
                {/* Category Header */}
                <div 
                  className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleCategory(catKey)}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900">{category.name}</h3>
                      <p className="text-sm text-gray-500">{category.description}</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500">
                    {enabledInCategory} / {category.modules.length} enabled
                  </span>
                </div>
                
                {/* Modules */}
                {isExpanded && (
                  <div className="border-t border-gray-100 divide-y divide-gray-100">
                    {category.modules.map((module) => {
                      const IconComponent = module.icon;
                      const isEnabled = enabledModules[module.key] === true;
                      const isDefault = isTierDefault(module.key);
                      const hasDeps = module.dependencies.length > 0;
                      const missingDeps = module.dependencies.filter(d => !enabledModules[d]);
                      
                      return (
                        <div
                          key={module.key}
                          className={`flex items-center justify-between px-5 py-4 ${
                            isEnabled ? 'bg-indigo-50/50' : ''
                          }`}
                          data-testid={`module-${module.key}`}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              isEnabled ? 'bg-indigo-100' : 'bg-gray-100'
                            }`}>
                              <IconComponent className={`w-5 h-5 ${
                                isEnabled ? 'text-indigo-600' : 'text-gray-400'
                              }`} />
                            </div>
                            
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-medium text-gray-900">{module.name}</h4>
                                
                                {/* Min Tier Badge */}
                                <span className={`px-2 py-0.5 text-xs rounded-full ${getTierBadgeColor(module.minTier)}`}>
                                  {module.minTier === 'enterprise_plus' ? 'E+' : 
                                   module.minTier.charAt(0).toUpperCase() + module.minTier.slice(1)}
                                </span>
                                
                                {/* Tier Default Badge */}
                                {isDefault && (
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                                    Tier Default
                                  </span>
                                )}
                              </div>
                              
                              <p className="text-sm text-gray-500 mt-0.5">{module.description}</p>
                              
                              {/* Dependencies */}
                              {hasDeps && (
                                <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                  <Info className="w-3 h-3" />
                                  Requires: {module.dependencies.map(d => 
                                    ALL_MODULES.find(m => m.key === d)?.name
                                  ).join(', ')}
                                  {missingDeps.length > 0 && !isEnabled && (
                                    <span className="text-yellow-600 ml-1">
                                      (Enable these first)
                                    </span>
                                  )}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Toggle Switch */}
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isEnabled}
                              onChange={() => toggleModule(module.key)}
                              className="sr-only peer"
                              data-testid={`toggle-${module.key}`}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
            <Info className="w-4 h-4" />
            Module Dependencies & Visibility
          </h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>Enabling a module automatically enables its dependencies.</li>
            <li>Disabled modules hide corresponding features from the tenant's navigation.</li>
            <li>Changes take effect after the tenant users refresh their browser.</li>
            <li>"Tier Default" modules are included in the tenant's subscription tier.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TenantModulesPage;
